/**
 * Ending the session (M0-F3 §5.4, parity card **P-038**).
 *
 * "Log out" in the avatar menu was a `<Link to="/logout">` and `/logout` was not in the route
 * table, so the click landed on the shell's `*` NotFound: no `auth.logout` request, the
 * `ezm_session` cookie intact, the shell still mounted and a reload still signed in. Two things
 * were wrong and both are fixed here — the row now *acts* (this helper), and `/logout` is a real
 * route that runs the same helper, because that is monday's own URL form and people bookmark it.
 *
 * The order matters:
 *
 *  1. `auth.logout` first, so the server stamps `revoked_at` and clears the cookie. Its failure
 *     is **not** a reason to stay signed in: a 401 means the session was already gone, and any
 *     other error still has to end in the browser being sent to `/login` — a person who clicked
 *     "Log out" must never be left sitting inside the app.
 *  2. The live socket is disconnected explicitly. The api also disconnects it from its side
 *     (`session.revoked` on the bus, F5 §5.4), but the client must not spend the reconnect
 *     attempts of a dead session waiting for it.
 *  3. The TanStack cache is cleared, so nothing that was read as the old user can be painted
 *     for the next one.
 *  4. `location.replace` — never `assign`, and never a client-side `navigate`. `replace` drops
 *     the app entry from the session history, which is what makes the back button not restore
 *     the app; a full document load also tears down every in-memory store the SPA held.
 */
import type { QueryClient } from '@tanstack/react-query';
import { disconnectSocket } from '../../lib/realtime/socket';
import type { TrpcClient } from '../../lib/trpc';

/** Where a signed-out browser lands. */
export const LOGIN_PATH = '/login';

export interface LogoutOptions {
  /** Swapped in tests. Defaults to `window.location.replace`. */
  redirect?: (path: string) => void;
}

/**
 * Revokes the session and leaves for `/login`. Resolves after the redirect has been asked for;
 * it never rejects, because there is no sensible "logout failed" state to leave a person in.
 */
export async function performLogout(
  client: Pick<TrpcClient, 'auth'>,
  queryClient: Pick<QueryClient, 'clear' | 'cancelQueries'>,
  options: LogoutOptions = {},
): Promise<void> {
  try {
    await client.auth.logout.mutate();
  } catch {
    /* already signed out, offline, or the api said no — we leave either way (see §1 above) */
  }
  try {
    disconnectSocket();
  } catch {
    /* no socket was ever opened on this page */
  }
  try {
    await queryClient.cancelQueries();
  } catch {
    /* cancelling is best effort; the clear below is what matters */
  }
  queryClient.clear();

  const redirect =
    options.redirect ??
    ((path: string) => {
      if (typeof window !== 'undefined') window.location.replace(path);
    });
  redirect(LOGIN_PATH);
}
